import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell } from '../../layout/Shell';
import { bulkImportFromExchange, listReplays } from '../../shared/api/client';
import { summaryFromApi } from '../../shared/api/transform';
import { Icon } from '../../shared/design/Icons';
import { Button, TierBadge } from '../../shared/design/Primitives';
import { useAsync } from '../../shared/hooks/useAsync';
import type { ApiBulkImportResult } from '../../shared/api/types';

export function HomePage() {
  const navigate = useNavigate();
  const [urlInput, setUrlInput] = useState('');
  // Bumping `refreshTick` re-runs the list query — used after a successful
  // bulk import so the recents strip + counter reflect the new rows.
  const [refreshTick, setRefreshTick] = useState(0);
  const { data, loading, error } = useAsync(() => listReplays(8), [refreshTick]);
  const recents = (data ?? []).map(summaryFromApi);

  const onAnalyze = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    navigate(`/scan?url=${encodeURIComponent(trimmed)}`);
  };

  return (
    <PageShell active="home">
      <div className="tm" style={{ maxWidth: 1280, margin: '0 auto', padding: '60px 32px 80px' }}>
        {/* Hero */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 60, alignItems: 'center', marginBottom: 64 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              TM·UTILS · v0.4 · GBX REPLAY INSPECTOR
            </div>
            <h1 className="h1" style={{ fontSize: 60, lineHeight: 1.02, marginBottom: 20 }}>
              Read what the car
              <br />
              <span style={{ color: 'var(--accent)' }}>actually did.</span>
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.55, color: 'var(--fg-2)', maxWidth: 560, marginBottom: 36 }}>
              Inspect any Trackmania replay's input telemetry — steering, throttle, brake, gear, RPM, speed, the lot. Spot action-key
              usage automatically.
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); onAnalyze(); }}
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--r-12)',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: 620,
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              }}
            >
              <div style={{ paddingLeft: 14, paddingRight: 4, color: 'var(--fg-3)' }}>
                <Icon.Link />
              </div>
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://trackmania.exchange/replays/download/96534"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--fg-1)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  padding: '12px 4px',
                }}
              />
              <Button variant="ghost" size="md" icon={<Icon.Upload />}>
                Upload .gbx
              </Button>
              <Button variant="primary" size="md" icon={<Icon.Scan />} onClick={onAnalyze}>
                Analyze
              </Button>
            </form>
            <div
              style={{
                marginTop: 14,
                fontSize: 12,
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <span>Paste from clipboard:</span>
              <span
                style={{ padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--fg-3)' }}
              >
                ⌘V
              </span>
              <span style={{ color: 'var(--fg-dim)' }}>·</span>
              <span>Accepts: trackmania.io · trackmania.exchange · .Replay.gbx</span>
            </div>
          </div>

          {/* Right column — placeholder card. Without a replay loaded we don't
              have a track to draw, so show the brand + the recents count. */}
          <div
            style={{
              position: 'relative',
              aspectRatio: '1 / 1',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-12)',
              padding: 18,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: 'var(--r-12)',
                  margin: '0 auto 18px',
                  background: 'linear-gradient(135deg, var(--accent), #00d4c4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#051019',
                }}
              >
                <Icon.Scan width={36} height={36} />
              </div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                Scanned so far
              </div>
              <div
                className="num"
                style={{ fontSize: 48, fontWeight: 600, color: 'var(--fg-1)', letterSpacing: '-0.02em' }}
              >
                {loading ? '—' : (data?.length ?? 0)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 6 }}>replays in the database</div>
            </div>
          </div>
        </div>

        {/* Bulk import from TMX */}
        <BulkImportCard onDone={() => setRefreshTick((n) => n + 1)} />

        {/* Recently analyzed */}
        <div style={{ marginBottom: 56 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 className="h2">Recently analyzed</h2>
            <button
              onClick={() => navigate('/leaderboard')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--fg-2)',
                fontSize: 13,
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              View leaderboard <Icon.Chevron />
            </button>
          </div>

          {error ? (
            <div
              style={{
                padding: '24px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--bad)',
                borderRadius: 'var(--r-8)',
                color: 'var(--bad)',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
              }}
            >
              Couldn't reach the replay API — {error.message}
            </div>
          ) : loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 132,
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-8)',
                  }}
                />
              ))}
            </div>
          ) : recents.length === 0 ? (
            <div
              style={{
                padding: '40px 24px',
                background: 'var(--bg-surface)',
                border: '1px dashed var(--border-strong)',
                borderRadius: 'var(--r-8)',
                textAlign: 'center',
                color: 'var(--fg-3)',
              }}
            >
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                Nothing scanned yet
              </div>
              <div style={{ fontSize: 13 }}>Paste a replay URL above to be the first.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              {recents.slice(0, 4).map((r) => (
                <button
                  key={r.ghostUid}
                  onClick={() => navigate(`/scan/${encodeURIComponent(r.ghostUid)}`)}
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-8)',
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--fg-3)',
                          fontFamily: 'var(--font-mono)',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {r.map}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', marginTop: 2 }}>
                        {r.author}
                      </div>
                    </div>
                    <TierBadge tier={r.tier} size="sm" label={false} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div className="num" style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg-1)' }}>
                      {r.raceTimeStr}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>
                      {r.topSpeedKmh} km/h
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* How it works */}
        <div>
          <h2 className="h2" style={{ marginBottom: 18 }}>
            How it works
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              {
                n: '01',
                t: 'Paste',
                d: 'Drop a replay URL from trackmania.io or upload a local .Replay.gbx file.',
                icon: <Icon.Link />,
                color: 'var(--accent)',
              },
              {
                n: '02',
                t: 'Parse',
                d: 'We read the GBX header and stream every input tick — steering, throttle, brake, gear, RPM, position.',
                icon: <Icon.Scan />,
                color: '#00d4c4',
              },
              {
                n: '03',
                t: 'Inspect',
                d: 'Telemetry charts, racing-line map, sector deltas. Suspicious clusters surface automatically.',
                icon: <Icon.Search />,
                color: '#ffe14a',
              },
            ].map((s) => (
              <div
                key={s.n}
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-8)',
                  padding: 22,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
                  <span className="num" style={{ fontSize: 28, fontWeight: 600, color: s.color, opacity: 0.9 }}>
                    {s.n}
                  </span>
                  <span style={{ color: s.color }}>{s.icon}</span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: 'var(--fg-1)' }}>{s.t}</div>
                <p className="p" style={{ color: 'var(--fg-2)' }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            marginTop: 80,
            paddingTop: 24,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'var(--fg-muted)',
            fontSize: 12,
          }}
        >
          <div>
            Created by <span style={{ color: 'var(--fg-2)' }}>Proxo</span> &{' '}
            <span style={{ color: 'var(--fg-2)' }}>SindreMA</span>
          </div>
          <div style={{ display: 'flex', gap: 18, fontFamily: 'var(--font-mono)' }}>
            <span>Source</span>
            <span>Discord</span>
            <span>About</span>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ============ Bulk import from TMX ============

// TMX's getonlinerecords endpoint caps responses at 50 records per call —
// pagination cursors don't get us past that — so 50 is our practical ceiling.
const IMPORT_COUNT_OPTIONS = [10, 25, 50];

function BulkImportCard({ onDone }: { onDone: () => void }) {
  const [url, setUrl] = useState('');
  const [count, setCount] = useState(10);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ApiBulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await bulkImportFromExchange(url.trim(), count);
      setResult(r);
      if (r.succeeded > 0) onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: 22,
        marginBottom: 32,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 className="h2" style={{ marginBottom: 4 }}>Bulk import from TMX</h2>
          <p className="p" style={{ marginBottom: 0 }}>
            Pull the top N replays for a trackmania.exchange map in one shot. Paste the mapshow URL or the numeric track ID.
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: 8,
          background: 'var(--bg-surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-8)',
        }}
      >
        <div style={{ paddingLeft: 14, paddingRight: 4, color: 'var(--fg-3)' }}>
          <Icon.Link />
        </div>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://trackmania.exchange/mapshow/309527"
          disabled={running}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--fg-1)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            padding: '10px 4px',
          }}
        />
        <select
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          disabled={running}
          style={{
            height: 36,
            padding: '0 10px',
            background: 'var(--bg-surface-3)',
            color: 'var(--fg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-6)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            cursor: running ? 'not-allowed' : 'pointer',
          }}
        >
          {IMPORT_COUNT_OPTIONS.map((n) => (
            <option key={n} value={n}>
              Top {n}
            </option>
          ))}
        </select>
        <Button variant="primary" size="md" icon={<Icon.ArrowDown />}>
          {running ? 'Importing…' : 'Import'}
        </Button>
      </form>

      {/* Status row */}
      {(running || result || error) && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: error ? 'var(--bad-12)' : result?.failed ? 'var(--warn-12)' : 'var(--ok-12)',
            border: `1px solid ${error ? 'var(--bad)' : result?.failed ? 'var(--warn)' : 'var(--ok)'}`,
            borderRadius: 'var(--r-6)',
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            color: error ? 'var(--bad)' : result?.failed ? 'var(--warn)' : 'var(--ok)',
          }}
        >
          {running && `Downloading ${count} replays from TMX — this can take a minute…`}
          {!running && error && `Import failed: ${error}`}
          {!running && result && (
            <>
              Imported <strong>{result.succeeded}</strong> of {result.requested}
              {result.failed > 0 && <> · {result.failed} failed</>}
              <span style={{ color: 'var(--fg-muted)', marginLeft: 8 }}>· track {result.trackId}</span>
            </>
          )}
        </div>
      )}

      {/* Per-item details when there were failures */}
      {result && result.failed > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--fg-3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            Per-replay results
          </summary>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {result.items.map((it) => (
              <div
                key={it.recordId}
                style={{
                  display: 'flex',
                  gap: 12,
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  color: it.ok ? 'var(--fg-2)' : 'var(--bad)',
                }}
              >
                <span style={{ width: 16 }}>{it.ok ? '✓' : '✗'}</span>
                <span style={{ width: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.recordId}</span>
                <span style={{ width: 140, color: 'var(--fg-1)' }}>{it.username || '—'}</span>
                <span style={{ flex: 1 }}>{it.ok ? it.ghostUid : it.error}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
